import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, User, Lock, Eye, EyeOff, TrendingUp, Users, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getApiErrorMessage } from '../shared/utils/apiError'

export default function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: true,
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await login(formData.email, formData.password)
      navigate('/app')
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'log in'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="h-screen max-h-screen w-full flex flex-col lg:flex-row bg-white font-sans antialiased overflow-hidden">
      {/* LEFT SIDE - Form: logo on top, form centred in the free space, footer at the bottom */}
      <div className="relative w-full lg:w-1/2 xl:w-[46%] h-full flex flex-col justify-between p-4 sm:p-6 lg:px-8 lg:py-5 xl:px-12 xl:py-6 bg-white overflow-hidden select-none sm:select-auto">
        {/* Soft lavender glow, matching the purple panel on the right */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#7C3AED]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -right-20 h-72 w-72 rounded-full bg-[#6E11E3]/10 blur-3xl" />

        {/* Header Bar */}
        <div className="relative flex items-center justify-between shrink-0 mb-1">
          <Link to="/" className="inline-flex items-center gap-2 group transition-transform duration-200 hover:scale-105">
            <span className="font-black text-2xl tracking-wider text-[#6E11E3]">ZYNX</span>
          </Link>
          <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full bg-[#F3E8FF] text-[#6E11E3] border border-[#E9D5FF] shadow-xs">
            WORKPLACE V2.0
          </span>
        </div>

        {/* Form block, perfectly centred between header and footer */}
        <div className="relative flex-1 flex items-center justify-center py-1 my-auto">
          <div className="w-full max-w-sm sm:max-w-md mx-auto">
            {/* Heading */}
            <div className="mb-3 sm:mb-4">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111827] tracking-tight mb-1">
                Welcome back
              </h1>
              <div className="h-1 w-10 rounded-full bg-gradient-to-r from-[#6E11E3] to-[#A855F7] mb-1.5" />
              <p className="text-xs sm:text-sm text-[#6B7280]">
                Sign in to continue to your Zynx workplace.
              </p>
            </div>

            {error && (
              <div role="alert" className="mb-3 flex items-start gap-2.5 p-2.5 text-xs sm:text-sm text-red-700 bg-red-50 rounded-xl border border-red-200 border-l-4 border-l-red-500 animate-in fade-in duration-200">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-3.5">
              <div>
                <label className="block text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#374151] mb-1">
                  USERNAME / EMAIL
                </label>
                <div className="relative group">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#9CA3AF] group-focus-within:text-[#6E11E3] transition-colors duration-150">
                    <User className="h-4 w-4" />
                  </span>
                  <input
                    type="text"
                    required
                    placeholder="Username or work email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 sm:py-2.5 bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl text-xs sm:text-sm text-[#111827] focus:bg-white focus:outline-none focus:border-[#6E11E3] focus:ring-4 focus:ring-[#6E11E3]/15 transition-all duration-150 placeholder:text-[#9CA3AF]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#374151] mb-1">
                  PASSWORD
                </label>
                <div className="relative group">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#9CA3AF] group-focus-within:text-[#6E11E3] transition-colors duration-150">
                    <Lock className="h-4 w-4" />
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-10 pr-10 py-2 sm:py-2.5 bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl text-xs sm:text-sm text-[#111827] focus:bg-white focus:outline-none focus:border-[#6E11E3] focus:ring-4 focus:ring-[#6E11E3]/15 transition-all duration-150 placeholder:text-[#9CA3AF]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-[#9CA3AF] hover:text-[#4B5563] transition-colors duration-150"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Remember & Forgot */}
              <div className="flex items-center justify-between text-xs pt-0.5">
                <label className="flex items-center gap-2 cursor-pointer text-[#4B5563] select-none hover:text-[#111827] transition-colors duration-150">
                  <input
                    type="checkbox"
                    checked={formData.remember}
                    onChange={(e) => setFormData({ ...formData, remember: e.target.checked })}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#6E11E3] focus:ring-[#6E11E3] transition-colors"
                  />
                  <span>Remember me</span>
                </label>
                <a href="#" className="font-semibold text-[#6E11E3] hover:text-[#5B21B6] hover:underline text-xs transition-colors duration-150">
                  Forgot password?
                </a>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 sm:py-3 px-6 rounded-xl font-bold text-xs sm:text-sm text-white bg-gradient-to-r from-[#5B21B6] via-[#6E11E3] to-[#7C3AED] hover:opacity-95 shadow-md hover:shadow-lg hover:shadow-[#6E11E3]/25 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] disabled:opacity-60 disabled:hover:scale-100 disabled:hover:translate-y-0 cursor-pointer"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <p className="flex items-center justify-center gap-1.5 text-[10px] sm:text-[11px] text-[#6B7280] pt-0.5">
                <ShieldCheck className="h-3.5 w-3.5 text-[#6E11E3]" aria-hidden="true" />
                Secure sign-in for Durrani Tech team members
              </p>
            </form>

            <p className="mt-2.5 sm:mt-3 text-xs text-center text-[#6B7280]">
              Having trouble signing in?{' '}
              <a href="https://www.durranitech.com/contact" target="_blank" rel="noreferrer" className="text-[#111827] font-semibold hover:text-[#6E11E3] hover:underline transition-colors duration-150">
                Contact your administrator.
              </a>
            </p>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="relative shrink-0 pt-1.5 border-t border-[#F3F4F6] flex items-center justify-between text-[11px] text-[#9CA3AF]">
          <span>© 2026 Zynx</span>
          <span>
            Powered by{' '}
            <a
              href="https://www.durranitech.com"
              target="_blank"
              rel="noreferrer"
              className="text-[#111827] font-semibold hover:text-[#6E11E3] transition-colors duration-150"
            >
              Durrani Tech
            </a>
          </span>
        </div>
      </div>

      {/* RIGHT SIDE - Rich Royal Purple Showcase (Fitted to viewport height without scrolling) */}
      <div className="hidden lg:flex w-full lg:w-[52%] xl:w-[55%] h-full bg-gradient-to-br from-[#5B13B8] via-[#6E11E3] to-[#7C3AED] relative p-5 lg:p-7 xl:p-8 flex-col justify-between items-center text-white overflow-hidden">
        {/* Background Dot Pattern & Ambient Orbs */}
        <div className="absolute inset-0 bg-dot-pattern opacity-25 pointer-events-none" />
        <div className="absolute top-8 left-8 grid grid-cols-3 gap-1.5 opacity-50">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/70" />
          ))}
        </div>
        <div className="absolute top-1/4 right-6 flex flex-col gap-2.5 opacity-60">
          <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />
          <div className="w-2 h-2 rounded-full bg-white/50" />
          <div className="w-2 h-2 rounded-full bg-white/50" />
        </div>

        {/* Top bar with back to home */}
        <div className="w-full flex justify-end shrink-0">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-semibold text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
        </div>

        {/* Center Mockup Visual with Floating Badges - Scaled for compact vertical fit */}
        <div className="relative my-auto w-full max-w-[420px] xl:max-w-[460px] scale-[0.92] sm:scale-95 xl:scale-100 transform origin-center">
          {/* Floating Pill Top Right: Revenue +24% (From Image 2) */}
          <div className="absolute -top-4 -right-3 z-20 bg-white rounded-xl p-2 sm:px-3.5 sm:py-2 shadow-xl border border-white/60 animate-float-slow">
            <div className="flex items-center gap-1.5">
              <div className="h-6 w-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <TrendingUp className="h-3.5 w-3.5 text-[#10B981]" />
              </div>
              <div>
                <p className="text-[9px] text-gray-500 font-medium uppercase tracking-wider leading-none">Revenue</p>
                <p className="text-xs font-extrabold text-gray-900 leading-tight">+24%</p>
              </div>
            </div>
          </div>

          {/* Floating Pill Bottom Left: Team 485 (From Image 2) */}
          <div className="absolute -bottom-4 -left-3 z-20 bg-white rounded-xl p-2 sm:px-3 sm:py-2 shadow-xl border border-white/60 animate-float-delayed">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-[#FAF5FF] text-[#6E11E3] flex items-center justify-center">
                <Users className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="text-[9px] text-gray-500 font-medium leading-none">Team</p>
                <p className="text-xs font-bold text-gray-900 leading-tight">485</p>
              </div>
            </div>
          </div>

          {/* White Dashboard Mockup Card (Direct match to Image 2) */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-2xl shadow-purple-950/40 border border-white/60 text-gray-800">
            {/* Window header dots */}
            <div className="flex items-center justify-between pb-3.5 border-b border-gray-100">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#EF4444]" />
                <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />
                <div className="w-2 h-2 rounded-full bg-[#10B981]" />
              </div>
              <div className="h-1.5 w-14 bg-gray-100 rounded-full" />
            </div>

            {/* Profile strip */}
            <div className="flex items-center gap-3 my-4">
              <div className="h-9 w-9 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center text-[#6E11E3]">
                <User className="h-4 w-4" />
              </div>
              <div className="space-y-1 flex-1">
                <div className="h-2.5 w-24 bg-[#8B5CF6]/30 rounded-full" />
                <div className="h-1.5 w-14 bg-gray-100 rounded-full" />
              </div>
            </div>

            {/* Progress Bars (78%, 62%, 91% from Image 2) */}
            <div className="space-y-3 my-4">
              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full bg-[#8B5CF6]" />
                <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div className="bg-[#8B5CF6] h-full rounded-full" style={{ width: '78%' }} />
                </div>
                <span className="text-[11px] font-bold text-gray-500 w-7 text-right">78%</span>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full bg-[#A855F7]" />
                <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div className="bg-[#A855F7] h-full rounded-full" style={{ width: '62%' }} />
                </div>
                <span className="text-[11px] font-bold text-gray-500 w-7 text-right">62%</span>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full bg-[#C4A0F5]" />
                <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div className="bg-[#C4A0F5] h-full rounded-full" style={{ width: '91%' }} />
                </div>
                <span className="text-[11px] font-bold text-gray-500 w-7 text-right">91%</span>
              </div>
            </div>

            {/* Modules Row (CRM, HRMS, ACC from Image 2) */}
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 text-center">
              <div>
                <p className="text-[11px] font-bold text-gray-900 leading-tight">CRM</p>
                <p className="text-[9px] text-gray-400">Sales</p>
              </div>
              <div>
                <p className="text-[11px] font-bold text-gray-900 leading-tight">HRMS</p>
                <p className="text-[9px] text-gray-400">People</p>
              </div>
              <div>
                <p className="text-[11px] font-bold text-gray-900 leading-tight">ACC</p>
                <p className="text-[9px] text-gray-400">Finance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Headline & Subtitle (from Image 2) */}
        <div className="text-center shrink-0 mt-2 max-w-sm">
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-white mb-0.5">
            Welcome to ZYNX
          </h2>
          <p className="text-[11px] sm:text-xs text-purple-100/85 leading-relaxed">
            Your unified workplace for CRM, HRMS, Accounts & more.
          </p>
        </div>
      </div>
    </div>
  )
}